import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  createCampaignFromTemplate,
  deleteTemplate,
  getTemplates,
  Template,
} from "../../../helpers/templates_helper";

const PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 350;

const stickyActionsStyle: React.CSSProperties = {
  position: "sticky",
  right: 0,
  background: "var(--vz-secondary-bg, var(--bs-body-bg, #fff))",
  boxShadow: "-6px 0 8px -6px rgba(0,0,0,.12)",
  zIndex: 1,
};

const stickyActionsHeadStyle: React.CSSProperties = {
  ...stickyActionsStyle,
  background: "var(--vz-table-header-bg, #f3f6f9)",
};

/**
 * Lista de templates do tenant (API /api/v1/templates).
 * Mesmo padrão de ícones da lista de campanhas (LAC-113).
 */
const TemplatesList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  document.title = t("templates.documentTitle");

  const helpHref = helpUrlForRoute("/templates", i18n.language);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<Template | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [creatingCampaignId, setCreatingCampaignId] = useState<number | null>(
    null
  );

  const hasActiveFilters = Boolean(appliedSearch);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTemplates({
        search: appliedSearch || undefined,
        page,
        per_page: PER_PAGE,
      });
      if (!res?.success) {
        setError(t("templates.error.load"));
        setTemplates([]);
        return;
      }
      setTemplates(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (e: any) {
      setError(e?.message || t("templates.error.load"));
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, t]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      setAppliedSearch((prev) => {
        if (prev === next) {
          return prev;
        }
        setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const confirmDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeletingBusy(true);
    setError(null);
    try {
      const res = await deleteTemplate(deleting.id);
      toast.success(res?.message || t("templates.toast.deleted"), {
        autoClose: 2500,
      });
      setDeleting(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("templates.error.delete"));
      setDeleting(null);
    } finally {
      setDeletingBusy(false);
    }
  };

  const onCreateCampaign = async (template: Template) => {
    setCreatingCampaignId(template.id);
    setError(null);
    try {
      const res = await createCampaignFromTemplate(template.id);
      toast.success(res?.message || t("templates.toast.campaignCreated"), {
        autoClose: 2500,
      });
      if (res?.data?.id) {
        navigate(`/campaigns/${res.data.id}/edit`);
        return;
      }
      await load();
    } catch (err: any) {
      setError(err?.message || t("templates.error.createCampaign"));
    } finally {
      setCreatingCampaignId(null);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setPage(1);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("templates.title")}
          actions={
            <PageHelpLink href={helpHref} label={t("templates.help")} />
          }
        />

        <ToastContainer closeButton={false} limit={1} />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex flex-wrap gap-2 flex-grow-1 align-items-center">
                  <div style={{ maxWidth: 320, flex: "1 1 220px" }}>
                    <Input
                      type="search"
                      placeholder={t("templates.search.placeholder")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label={t("templates.search.placeholder")}
                    />
                  </div>
                  {hasActiveFilters && (
                    <Button
                      color="link"
                      className="text-decoration-underline p-0"
                      onClick={clearFilters}
                    >
                      {t("templates.filter.clear")}
                    </Button>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    color="soft-primary"
                    tag={Link}
                    to="/templates/gallery"
                  >
                    {t("templates.galleryCta")}
                  </Button>
                  <Button color="primary" tag={Link} to="/templates/new">
                    {t("templates.create")}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-5">
                    {!hasActiveFilters && (
                      <div className="avatar-lg mx-auto mb-3">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-1">
                          <i className="ri-layout-5-line" />
                        </div>
                      </div>
                    )}
                    <h5 className="mb-2">
                      {hasActiveFilters
                        ? t("templates.empty.filtered.title")
                        : t("templates.empty.title")}
                    </h5>
                    <p className="text-muted mb-3">
                      {hasActiveFilters
                        ? t("templates.empty.filtered.description")
                        : t("templates.empty.description")}
                    </p>
                    {hasActiveFilters ? (
                      <Button color="light" onClick={clearFilters}>
                        {t("templates.filter.clear")}
                      </Button>
                    ) : (
                      <div className="d-flex flex-wrap gap-2 justify-content-center">
                        <Button color="primary" tag={Link} to="/templates/new">
                          {t("templates.empty.cta")}
                        </Button>
                        <Button
                          color="soft-primary"
                          tag={Link}
                          to="/templates/gallery"
                        >
                          {t("templates.empty.galleryCta")}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-muted mb-3">
                      {t("templates.totalCount", { count: total })}
                    </p>
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("templates.table.name")}</th>
                            <th className="text-nowrap" style={{ width: 160 }}>
                              {t("templates.table.updatedAt")}
                            </th>
                            <th
                              className="text-end text-nowrap"
                              style={{ width: 140, ...stickyActionsHeadStyle }}
                              aria-label={t("templates.table.actions")}
                            />
                          </tr>
                        </thead>
                        <tbody>
                          {templates.map((template) => (
                            <tr key={template.id}>
                              <td>
                                <Link
                                  to={`/templates/${template.id}/edit`}
                                  className="fw-medium text-body text-decoration-none"
                                  title={t("templates.edit.button")}
                                >
                                  {template.name}
                                </Link>
                              </td>
                              <td className="text-muted text-nowrap">
                                {template.updated_at
                                  ? new Date(
                                      template.updated_at
                                    ).toLocaleString()
                                  : "—"}
                              </td>
                              <td
                                className="text-end text-nowrap"
                                style={stickyActionsStyle}
                              >
                                <Button
                                  color="soft-success"
                                  size="sm"
                                  className="btn-icon"
                                  disabled={creatingCampaignId === template.id}
                                  onClick={() => onCreateCampaign(template)}
                                  title={t("templates.actions.createCampaign")}
                                  aria-label={t(
                                    "templates.actions.createCampaign"
                                  )}
                                >
                                  {creatingCampaignId === template.id ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <i
                                      className="ri-mail-add-line"
                                      aria-hidden
                                    />
                                  )}
                                </Button>
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  className="btn-icon ms-1"
                                  tag={Link}
                                  to={`/templates/${template.id}/edit`}
                                  title={t("templates.edit.button")}
                                  aria-label={t("templates.edit.button")}
                                >
                                  <i className="ri-pencil-fill" aria-hidden />
                                </Button>
                                <UncontrolledDropdown className="d-inline-block ms-1">
                                  <DropdownToggle
                                    tag="button"
                                    type="button"
                                    className="btn btn-soft-secondary btn-sm btn-icon fw-bold"
                                    title={t("templates.actions.more")}
                                    aria-label={t("templates.actions.more")}
                                  >
                                    <span aria-hidden>:</span>
                                  </DropdownToggle>
                                  <DropdownMenu end>
                                    <DropdownItem
                                      className="text-danger"
                                      onClick={() => setDeleting(template)}
                                    >
                                      <i
                                        className="ri-delete-bin-line me-2"
                                        aria-hidden
                                      />
                                      {t("templates.delete.button")}
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {lastPage > 1 && (
                      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-3">
                        <span className="text-muted small">
                          {t("templates.pagination.label", {
                            page,
                            lastPage,
                            total,
                          })}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            {t("templates.pagination.prev")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page >= lastPage}
                            onClick={() =>
                              setPage((p) => Math.min(lastPage, p + 1))
                            }
                          >
                            {t("templates.pagination.next")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={!!deleting}
        toggle={() => !deletingBusy && setDeleting(null)}
        centered
      >
        <ModalHeader toggle={() => !deletingBusy && setDeleting(null)}>
          {t("templates.delete.title")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            {t("templates.delete.confirm", { name: deleting?.name })}
          </p>
          <p className="text-muted mb-0 small">
            {t("templates.delete.consequence")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={deletingBusy}
            onClick={() => setDeleting(null)}
          >
            {t("templates.form.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={deletingBusy}
            onClick={confirmDelete}
          >
            {deletingBusy ? (
              <Spinner size="sm" />
            ) : (
              t("templates.delete.confirmButton")
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TemplatesList;
